// @ts-nocheck - Temporarily ignore type errors while fixing Redux context flow
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import fetchPassengerNotification from "shared/cbt/api/fetchPassengerNotification";
import { initBooking } from "shared/data/actions/booking";
import { removeMobileFormsValue } from "shared/data/actions/mobileForms";
import { deleteActualFlightServices } from "shared/data/actions/search";
import { getCbtToken } from "shared/data/customerTokens";
import { setPrice } from "shared/lib/helperFunctions";
import Logger from "shared/services/Logger";

import {
  setBookingFormActualRefs,
  setBookingFormValue,
} from "../data/actions/bookingForm";
import { addAirportDestinations } from "../data/actions/destinations";
import { removeFormValue, setFormValue } from "../data/actions/forms";
import { stopUniversalLoading } from "../data/actions/loader";
import {
  resetPriceState,
  setCurrency,
  setPaymentPrice,
} from "../data/actions/price";
import {
  setAirplanes,
  setAirports,
  setTransportCompanies,
} from "../data/actions/search";
import { getLoyaltyPrograms } from "../data/actions/storage";
import {
  baggageInfoToBaggageLimit,
  extractBaggageFromPriceActualization,
  extractBaggageFromSearchResults,
  extractBaggageFromUrl,
  updateUrlWithBaggageInfo,
} from "../lib/baggageUtils";
import {
  getFlightOfferDestinationCode,
  parseAirplanes,
  parseAirport,
  parseTransportCompanies,
} from "../lib/functions";
import { fetchAcquisitionParams, priceActualization } from "../lib/golCopy";
import { isMobile } from "../lib/isMobile";
import {
  getMissingActualFlightOfferFallback,
  hasActualFlightOffer,
} from "../lib/priceActualizationGuards";
import { isRyanairFlightOffer } from "../lib/ryanair";

const enhanceFlightOfferWithBaggage = (
  flightOffer,
  priceActualizationResponse,
  searchState,
  refs
) => {
  if (!flightOffer?.PricingDetails?.PricingDetail?.[0]?.BaggageLimit) {
    Logger.info("No BaggageLimit found in flight offer, returning original");
    return flightOffer;
  }

  const currentBaggageLimit =
    flightOffer.PricingDetails.PricingDetail[0].BaggageLimit;

  const urlBaggage = !isMobile()
    ? extractBaggageFromUrl(new URLSearchParams(window.location.search))
    : null;

  const searchBaggage = extractBaggageFromSearchResults(searchState, refs);

  const priceActualizationBaggage = extractBaggageFromPriceActualization({
    GolApi: priceActualizationResponse,
  });

  let bestBaggageInfo = null;

  // First priority: Use URL parameters if available (set when navigating to result page)
  if (urlBaggage) {
    bestBaggageInfo = urlBaggage;
    Logger.info("Using baggage info from URL parameters");
  }
  // Second priority: Check baggage info from flight search (complete data only)
  else if (
    searchBaggage &&
    searchBaggage.Weight &&
    searchBaggage.Weight !== "0" &&
    searchBaggage.Quantity &&
    searchBaggage.Quantity !== "0" &&
    searchBaggage.Included !== "Unknown"
  ) {
    bestBaggageInfo = searchBaggage;
    Logger.info("Using baggage info from search results");
  }
  // Third priority: Check baggage info from price actualization
  else if (priceActualizationBaggage) {
    bestBaggageInfo = priceActualizationBaggage;
    Logger.info("Using baggage info from price actualization");
  }
  // Fallback: Use incomplete search baggage if available
  else if (searchBaggage) {
    bestBaggageInfo = searchBaggage;
    Logger.info("Using incomplete baggage info from search results");
  }

  if (!bestBaggageInfo) {
    Logger.info("No baggage info found, returning original flight offer");
    return flightOffer;
  }

  if (
    !urlBaggage &&
    bestBaggageInfo.source === "priceActualization" &&
    !isMobile()
  ) {
    updateUrlWithBaggageInfo(bestBaggageInfo);
  }

  const enhancedBaggageLimit = baggageInfoToBaggageLimit(bestBaggageInfo);

  if (!enhancedBaggageLimit) {
    return flightOffer;
  }

  const shouldEnhance =
    bestBaggageInfo.source === "priceActualization" ||
    bestBaggageInfo.source === "url" ||
    (bestBaggageInfo.source === "search" &&
      (!currentBaggageLimit.Weight ||
        !currentBaggageLimit.Quantity ||
        currentBaggageLimit.Included === "Unknown"));

  if (!shouldEnhance) {
    Logger.info("No enhancement needed, returning original flight offer");
    return flightOffer;
  }

  Logger.info(
    "Enhancing flight offer with baggage info from:",
    bestBaggageInfo.source
  );

  return {
    ...flightOffer,
    PricingDetails: {
      ...flightOffer.PricingDetails,
      PricingDetail: flightOffer.PricingDetails.PricingDetail.map(
        (detail, index) => {
          if (index === 0) {
            return {
              ...detail,
              BaggageLimit: {
                ...currentBaggageLimit,
                ...enhancedBaggageLimit,
              },
            };
          }
          return detail;
        }
      ),
    },
  };
};

export const useResult = ({
  getRefs,
  showBrandModal,
  showNotFoundError,
  showCustomError,
  router = null,
}) => {
  const dispatch = useDispatch();
  const actualFlightServices = useSelector(
    (state) => state.actualFlightServices
  );
  const frontendSettings = useSelector(
    (state) => state.storage.frontendSettings
  );
  const user = useSelector((state) => state.user);
  const cbt = useSelector((state) => state.cbt);
  const additionalServices = useSelector((state) => state.additionalServices);
  const searchState = useSelector((state) => state.search);

  const customerUsername = user?.email;
  const cbtApiUrl = frontendSettings?.dealerCorporateSettings?.cbtApiUrl;
  const promoCode = useSelector((state) => state.bookingForm.promoCode);
  const actualRefs = useSelector((state) => state.bookingForm.actualRefs);
  const defaultCountry =
    frontendSettings?.dealerCorporateSettings?.defaultCountry || "";

  const [actualFlightOffer, setActualFlightOffer] = useState({});

  const [
    passengerNotificationMessages,
    setPassengerNotificationMessages,
  ] = useState({
    isLoading: true,
    messages: [],
  });
  const [refs, setRefs] = useState([]);

  const isAppMobile = isMobile();

  useEffect(() => {
    dispatch(initBooking());

    return () => {
      dispatch(setBookingFormValue("promoCode", null));
      dispatch(deleteActualFlightServices());
    };
  }, [dispatch, refs, actualRefs]);

  const componentDidFocus = useCallback(async () => {
    dispatch(setBookingFormValue("isRyanairReservation", false));
    dispatch(setBookingFormValue("isRyanairConditionsConfirmed", false));
    await getLoyaltyPrograms();
    await resetServices();
    const actualData = await getActualData();
    if (!actualData) {
      if (
        !isAppMobile &&
        window?.location?.search?.includes("brandsDetailsAvailable=true")
      ) {
        showBrandModal();
        return true;
      }

      if (isAppMobile && router?.params?.brandsDetailsAvailable) {
        showBrandModal();
        return true;
      }

      return true;
    }

    const acquisitionParams = await fetchAcquisitionParamsFunc(actualData);
    if (!acquisitionParams) {
      showNotFoundError();
      return true;
    }

    const isCbtUser = await getCbtToken();

    if (isCbtUser) {
      await handleCBTPassengerNotification(cbt, actualData);
    } else {
      setPassengerNotificationMessages({
        isLoading: false,
        messages: [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPriceWithPromoCode = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-shadow
    async (promoCode = "") => {
      const currentRefs = getRefs();
      let alternativeCurrency = null;

      if (!isAppMobile) {
        const urlParams = new URLSearchParams(window.location.search);
        alternativeCurrency = urlParams.get("currency");
      }

      const actualData = await priceActualization({
        refs: actualRefs?.length === 0 ? currentRefs : actualRefs,
        promoCode,
        alternativeCurrency,
      });

      if (!hasActualFlightOffer(actualData)) {
        return { promoCodeWarning: null };
      }

      const isRyanairReservation = isRyanairFlightOffer(
        actualData.actualFlightOffer
      );

      dispatch(
        setBookingFormValue("isRyanairReservation", isRyanairReservation)
      );

      if (!isRyanairReservation) {
        dispatch(setBookingFormValue("isRyanairConditionsConfirmed", false));
      }

      const mergedRefs =
        actualRefs && actualRefs.length > 0
          ? [...actualData.refs, ...actualRefs.slice(actualData.refs.length)]
          : [...actualData.refs];

      await dispatch(setBookingFormActualRefs(mergedRefs));

      setPrice(actualData.actualFlightOffer, dispatch);
      dispatch(
        setBookingFormValue(
          "lastPaymentDate",
          actualData.actualFlightOffer.PaymentConditions.LastPaymentDate
        )
      );
      dispatch(setPaymentPrice(0));

      const enhancedFlightOffer = enhanceFlightOfferWithBaggage(
        actualData.actualFlightOffer,
        actualData.fullResponse,
        searchState,
        currentRefs
      );

      setActualFlightOffer(enhancedFlightOffer);

      const promoCodeWarnings =
        actualData.fullResponse?.Warnings?.Warning || [];

      const PROMO_CODE_WARNING_SUCCESS = "50-32";
      const PROMO_CODE_WARNING_ERROR = "50-33";
      if (
        promoCodeWarnings.some(
          (warning) => warning.Code === PROMO_CODE_WARNING_SUCCESS
        )
      ) {
        await dispatch(setBookingFormValue("promoCode", promoCode));

        return {
          promoCodeWarning: promoCodeWarnings.find(
            (warning) => warning.Code === PROMO_CODE_WARNING_SUCCESS
          ),
          originalPrice: actualData.actualFlightOffer.FlightPrice.FullPrice,
          discountPrice: actualData.actualFlightOffer.FlightPrice.ServicesPrice,
          refs: mergedRefs,
        };
      }

      if (
        promoCodeWarnings.some(
          (warning) => warning.Code === PROMO_CODE_WARNING_ERROR
        )
      ) {
        await dispatch(setBookingFormValue("promoCode", null));

        return {
          originalPrice: actualData.actualFlightOffer.FlightPrice.FullPrice,
          promoCodeWarning: promoCodeWarnings.find(
            (warning) => warning.Code === PROMO_CODE_WARNING_ERROR
          ),
          promoCodePopupErrorMessage:
            promoCodeWarnings.find(
              (warning) =>
                ![
                  PROMO_CODE_WARNING_SUCCESS,
                  PROMO_CODE_WARNING_ERROR,
                ].includes(warning.Code)
            )?.$t || null,
          refs: mergedRefs,
        };
      }

      return {
        promoCodeWarning: null,
        refs: mergedRefs,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, getRefs, isAppMobile, actualRefs]
  );

  const getActualData = useCallback(async () => {
    const currentRefs = getRefs();
    let alternativeCurrency = null;

    if (!isAppMobile) {
      const urlParams = new URLSearchParams(window.location.search);
      alternativeCurrency = urlParams.get("currency");
    }

    const actualData = await priceActualization({
      refs: currentRefs,
      promoCode: null,
      alternativeCurrency,
    });

    if (actualData?.error) {
      dispatch(setBookingFormValue("isRyanairReservation", false));
      dispatch(setBookingFormValue("isRyanairConditionsConfirmed", false));

      if (isAppMobile) {
        if (actualData.errorCode !== "3000") {
          showNotFoundError();
        }
      } else {
        showCustomError("Alert.flightNotFound");
      }
      return false;
    }

    if (!hasActualFlightOffer(actualData)) {
      const missingOfferFallback = getMissingActualFlightOfferFallback({
        isAppMobile,
        brandsDetailsAvailable: !isAppMobile
          ? window?.location?.search?.includes("brandsDetailsAvailable=true")
          : Boolean(router?.params?.brandsDetailsAvailable),
      });

      if (missingOfferFallback === "notFoundError") {
        showNotFoundError();
      } else if (missingOfferFallback === "customFlightNotFoundError") {
        showCustomError("Alert.flightNotFound");
      }

      return false;
    }

    const isRyanairReservation = isRyanairFlightOffer(
      actualData.actualFlightOffer
    );

    dispatch(setBookingFormValue("isRyanairReservation", isRyanairReservation));

    if (!isRyanairReservation) {
      dispatch(setBookingFormValue("isRyanairConditionsConfirmed", false));
    }

    dispatch(
      setBookingFormValue(
        "lastPaymentDate",
        actualData.actualFlightOffer.PaymentConditions.LastPaymentDate
      )
    );

    dispatch(addAirportDestinations(actualData.CodeBook.Airports.Airport));
    setPrice(actualData.actualFlightOffer, dispatch);
    dispatch(setCurrency(actualData.currency));

    const airplanes = parseAirplanes(actualData.CodeBook);
    dispatch(setAirplanes(airplanes));
    dispatch(stopUniversalLoading());

    if (!isAppMobile) {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("isSpecialOffer") === "true") {
        if (refs.join() !== actualData.refs.join()) {
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.set("refs", actualData.refs);
          window.history.replaceState({}, "", newUrl.toString());
        }
      }
    }

    const enhancedFlightOffer = enhanceFlightOfferWithBaggage(
      actualData.actualFlightOffer,
      actualData.fullResponse,
      searchState,
      currentRefs
    );

    setActualFlightOffer(enhancedFlightOffer);
    setRefs(actualData.refs);
    dispatch(setBookingFormActualRefs(actualData.refs));

    dispatch(
      setTransportCompanies(parseTransportCompanies(actualData.CodeBook))
    );

    dispatch(setAirports(parseAirport(actualData.CodeBook)));

    return actualData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getRefs, isAppMobile, dispatch, refs, searchState]);

  const fetchAcquisitionParamsFunc = useCallback(
    async (data) => {
      const refsResolved = [
        ...(data.refs ?? []),
        ...(actualFlightServices ?? []),
      ];

      const flightOffer = data?.actualFlightOffer;

      let alternativeCurrency = null;
      if (!isAppMobile) {
        const urlParams = new URLSearchParams(window.location.search);
        alternativeCurrency = urlParams.get("currency");
      }

      // @ts-ignore - Handle type safety in the dynamic data context
      const acquisitionData = await fetchAcquisitionParams({
        refs: refsResolved,
        fullPrice: flightOffer?.FlightPrice?.FullPrice,
        defaultCountry: defaultCountry || "",
        promoCode: promoCode || null,
        alternativeCurrency,
      });

      if (!acquisitionData) {
        return false;
      }

      if (acquisitionData.ticketingCombinations.length === 0) {
        showCustomError("Result.noTicketingCombinations");
      }
      dispatch(setFormValue("codebook", acquisitionData.codebook));

      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      actualFlightOffer,
      actualFlightServices,
      cbtApiUrl,
      customerUsername,
      defaultCountry,
      dispatch,
      frontendSettings,
      promoCode,
      user,
      isAppMobile,
    ]
  );

  const resetServices = useCallback(async () => {
    const additionalServicesIndexes = Object.keys(additionalServices || {});
    additionalServicesIndexes.forEach((index) => {
      const passengerNum = index.charAt(0);
      const additionalServicesType = Object.keys(additionalServices[index]);
      additionalServicesType.forEach((type) => {
        Object.keys(additionalServices[index][type]).forEach((key) => {
          const name = `${passengerNum}-${key}`;
          dispatch(removeFormValue(name));
          dispatch(removeMobileFormsValue(name));
        });
      });
    });
    await dispatch(resetPriceState());
  }, [additionalServices, dispatch]);

  const handleCBTPassengerNotification = useCallback(
    async (cbtData, actualData) => {
      const cbtToken = await getCbtToken();
      const marketingAirlines = [];
      const flightOffer = actualData?.actualFlightOffer;

      const CBTConnectionData = { cbtToken, cbtApiUrl, customerUsername };
      const destinationCode = getFlightOfferDestinationCode(flightOffer);
      flightOffer?.FlightItinerary?.FlightStream?.forEach((flightStream) => {
        flightStream.FlightOption.forEach((flightOption) => {
          flightOption.FlightSegments.FlightSegment.forEach((flightSegment) => {
            marketingAirlines.push(flightSegment.MarketingAirline);
          });
        });
      });

      const notifications = await fetchPassengerNotification(
        cbtData,
        CBTConnectionData,
        destinationCode,
        "Flight",
        marketingAirlines
      );

      setPassengerNotificationMessages(
        notifications
          ? {
              isLoading: false,
              messages: notifications,
            }
          : {
              isLoading: false,
              messages: [],
            }
      );
    },
    [cbtApiUrl, customerUsername]
  );

  return {
    actualFlightOffer,
    componentDidFocus,
    refs: refs?.join("---"),
    fullPrice: actualFlightOffer?.FlightPrice?.FullPrice,
    passengerNotificationMessages,
    getPriceWithPromoCode,
  };
};

export default useResult;
